import { zodResolver } from "@hookform/resolvers/zod";
import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import {
  getControlName,
  service,
  useAuthConfig,
  cn,
  parseDate,
  formatDate,
  TimeFormats,
} from "@theprelude/core";
import {
  AlertIcon,
  Button,
  CheckmarkIcon,
  CloseIcon,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ControlIcon,
  FunnelIcon,
  IconButton,
  notifyError,
  RotatingChevron,
  RouteIcon,
  ServerErrorIcon,
  ShieldFile,
  Truncate,
  useCollapsible,
  notifySuccess,
  TrashcanIcon,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EditorIcon,
  LoaderIcon,
  FileUploadIcon,
  Form,
  InputFormField,
} from "@theprelude/ds";
import {
  ControlCategories,
  ControlCodes,
  ControlMonitorEndpointSummary,
  PartnerEvents,
  SCMThreat,
  UpdateThreatParams,
} from "@theprelude/sdk";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AggregateTechniqueSummary,
  TechniqueSummary,
  aggregateTechniqueSummaryQuery,
  individualTechniqueSummaryQuery,
  useAggregateTechniqueSummary,
  useExceptedFilters,
} from "@/queries/control-monitor";
import { DefaultTooltip } from "../../default-tooltip";
import { useTable } from "../../table/hooks";
import { NotApplicable } from "../../table/unavaliable";
import { VirtualTable } from "../../table/virtual-table";
import { useControlMonitorNavigation } from "../control-monitor-navigation";
import { ThreatSelector } from "../threat-selector";

function MisconfigurationCell({ count }: { count: number | undefined }) {
  if (count === undefined) {
    return <NotApplicable />;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1 font-medium",
        count > 0 ? "text-status-red" : "text-status-green",
      )}
    >
      {count > 0 ? (
        <AlertIcon className="size-4 text-status-red" />
      ) : (
        <CheckmarkIcon className="size-4 text-status-green" />
      )}
      {count}
    </div>
  );
}

function FilterTechnique({
  id,
  onClick,
}: {
  id: string;
  onClick: (event: unknown) => void;
}) {
  const authConfig = useAuthConfig();
  const router = useRouterState();
  const { showExcepted } = useExceptedFilters();

  const fetches = useIsFetching(
    aggregateTechniqueSummaryQuery(authConfig, {
      filters: router.location.search.filters,
      threatId: router.location.search.threat_id,
      focusTechnique: id,
      showExcepted,
    }),
  );

  const isLoading = fetches > 0;
  return (
    <DefaultTooltip asChild content="Evaluate by technique">
      <IconButton
        loading={isLoading}
        icon={
          <FunnelIcon className="group-[.selected]:text-primary-foreground" />
        }
        onClick={onClick}
      />
    </DefaultTooltip>
  );
}

const columnHelper = createColumnHelper<TechniqueSummary>();
const columns = [
  columnHelper.accessor("id", {
    header: "ATT&CK ID",
    enableHiding: false,
    meta: {
      widths: ["110px", "1fr"],
      label: "ATT&CK ID",
    },
  }),
  columnHelper.accessor("name", {
    header: "Name",
    enableHiding: false,
    cell: (info) => {
      return <Truncate text={info.getValue()}>{info.getValue()}</Truncate>;
    },
    meta: {
      widths: ["150px", "2fr"],
      label: "Name",
    },
  }),
  columnHelper.accessor("misconfigurationCount", {
    id: "misconfiguration",
    sortUndefined: "last",
    cell: (info) => {
      return <MisconfigurationCell count={info.getValue()} />;
    },
    meta: {
      label: "Status",
      widths: ["50px", "1fr"],
    },
  }),
  columnHelper.display({
    id: "filter",
    cell: ({ row }) => {
      if (row.original.misconfigurationCount === undefined) {
        return null;
      }

      return (
        <FilterTechnique
          id={row.original.id}
          onClick={row.getToggleSelectedHandler()}
        />
      );
    },
    meta: {
      widths: ["30px", "30px"],
    },
  }),
];

const getMissingEDRCount = (
  endpointSummary: ControlMonitorEndpointSummary,
): number => {
  const xdr = endpointSummary.categories.find(
    (c) => c.category === ControlCategories.XDR,
  );
  return endpointSummary.endpoint_count - (xdr?.endpoint_count ?? 0);
};

function LinkToControlFailure({ onClick }: { onClick: () => void }) {
  const router = useRouterState();
  const { data: aggregateSummary } = useAggregateTechniqueSummary({
    threatId: router.location.search.threat_id,
  });
  const node = aggregateSummary?.mostConcerningControlFailureNode;

  const { view } = useControlMonitorNavigation();

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1 text-sm font-medium">
        <ServerErrorIcon className="size-4 text-secondary-foreground" /> Control
        Failures
      </div>

      {node && (
        <div>
          <Button
            intent="text-link"
            onClick={() => {
              onClick();
              void view(node, { technique: undefined });
            }}
            className="inline-flex items-center gap-1 p-0 text-sm text-status-red no-underline font-medium hover:font-bold"
          >
            <AlertIcon className="size-4" />
            {aggregateSummary?.summary.endpoint_summary.categories
              .reduce(
                (sum, c) => sum + (c.excepted?.control_failure_count ?? 0),
                0,
              )
              .toLocaleString()}
          </Button>
        </div>
      )}
    </div>
  );
}

const EMPTY_ARRAY: never[] = [];

function ThreatViewDetails() {
  const router = useRouterState();
  const authConfig = useAuthConfig();
  const collapsible = useCollapsible(true);
  const { showExcepted } = useExceptedFilters();

  const { data: aggregateSummary } = useSuspenseQuery(
    aggregateTechniqueSummaryQuery(authConfig, {
      threatId: router.location.search.threat_id,
      filters: router.location.search.filters,
      showExcepted,
    }),
  );

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-stroke-line px-3 py-2">
        <h2 className="flex items-center gap-2 font-medium">
          <RouteIcon className="size-4 text-secondary-foreground" />
          Evaluate against a threat
        </h2>
        <Link
          to="/control-monitor"
          search={(prev) => ({
            ...prev,
            threat_id: undefined,
            technique: undefined,
          })}
        >
          <IconButton className="p-0" icon={<CloseIcon />} />
        </Link>
      </div>
      <div className="flex flex-col gap-2 p-4">
        {aggregateSummary.threat && (
          <ThreatTitle threat={aggregateSummary.threat} />
        )}

        {aggregateSummary.threat?.account_id !== "prelude" &&
          aggregateSummary.threat?.created && (
            <div className="flex items-center gap-1 text-sm text-secondary-foreground font-medium">
              <FileUploadIcon className="size-4" />
              <span>
                Uploaded on{" "}
                {formatDate(
                  parseDate(aggregateSummary.threat.created),
                  TimeFormats.DATE_FIRST,
                )}
              </span>
            </div>
          )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-sm font-medium">
            <ControlIcon
              control={ControlCodes.NONE}
              className="size-4 text-secondary-foreground"
            />
            {getControlName(ControlCodes.NONE)}
          </div>
          <div>
            <Link
              to="/control-monitor"
              search={(prev) => ({
                ...prev,
                active_id: "no-edr",
                technique: undefined,
                overlay: [
                  "control-category-failures",
                  {
                    category: ControlCategories.XDR,
                    partnerEvent: PartnerEvents.NO_EDR,
                    filters: [],
                  },
                ],
              })}
              onClick={() => collapsible.onOpenChange(false)}
              className="inline-flex items-center gap-1 text-sm text-status-red font-medium hover:font-bold"
            >
              <AlertIcon className="size-4" />
              {getMissingEDRCount(
                aggregateSummary.summary.endpoint_summary,
              ).toLocaleString()}
            </Link>
          </div>
        </div>
        <LinkToControlFailure onClick={() => collapsible.onOpenChange(false)} />

        <ThreatViewTechniques
          open={collapsible.open}
          onOpenChange={collapsible.onOpenChange}
          aggregateSummary={aggregateSummary}
        />
        {aggregateSummary.threat?.id &&
          aggregateSummary.threat.account_id !== "prelude" && (
            <DeleteThreatButton threatId={aggregateSummary.threat?.id} />
          )}
      </div>
    </Card>
  );
}

function EditThreatName({
  threat,
  onDone,
}: {
  threat: SCMThreat;
  onDone: () => void;
}) {
  const form = useForm({
    defaultValues: {
      name: threat.name,
    },
    resolver: zodResolver(
      z.object({
        name: z.string().nonempty({ message: "Threat name is required" }),
      }),
    ),
  });

  const { register } = form;

  const authConfig = useAuthConfig();
  const updateThreat = useMutation({
    mutationFn: (data: UpdateThreatParams) =>
      service(authConfig).scm.updateThreat(threat.id, data),
    onSuccess: () => {
      notifySuccess({ message: "Threat name updated successfully" });
      onDone();
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to update the threat",
        error,
      });
    },
  });

  return (
    <Form
      {...form}
      className="flex items-center gap-2"
      onSubmitValid={(data) => {
        updateThreat.mutate({ name: data.name });
      }}
    >
      <div className="flex-1">
        <InputFormField
          {...register("name")}
          className="min-w-full"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onDone();
            }
          }}
        />
      </div>
      <DefaultTooltip content="Rename">
        <IconButton
          type="submit"
          icon={
            updateThreat.isPending ? (
              <LoaderIcon className="size-4" />
            ) : (
              <CheckmarkIcon className="size-4" />
            )
          }
          loading={updateThreat.isPending}
          disabled={updateThreat.isPending}
        />
      </DefaultTooltip>
    </Form>
  );
}

function ThreatTitle({ threat }: { threat: SCMThreat }) {
  const [edit, setEdit] = useState(false);

  if (edit) {
    return <EditThreatName threat={threat} onDone={() => setEdit(false)} />;
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <h3 className="w-full text-lg font-medium">
        <Truncate maxLines={2} text={threat.name}>
          {threat.name}
        </Truncate>
      </h3>
      {threat.account_id !== "prelude" && (
        <DefaultTooltip content="Rename">
          <IconButton
            onClick={() => setEdit(true)}
            icon={<EditorIcon />}
            className="p-0"
          />
        </DefaultTooltip>
      )}
    </div>
  );
}

function DeleteThreatButton({ threatId }: { threatId: string }) {
  const authConfig = useAuthConfig();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);

  const deleteThreat = useMutation({
    mutationFn: () => service(authConfig).scm.deleteThreat(threatId),
    onSuccess: () => {
      notifySuccess({ message: "Threat deleted successfully" });
      void navigate({
        to: "/control-monitor",
        search: (prev) => ({ ...prev, threat_id: undefined }),
      });
    },
    onError: () => {
      notifyError({
        message: "Error occurred while trying to delete the threat",
      });
    },
  });

  return (
    <>
      <Button
        intent="negative-inverted"
        icon={<TrashcanIcon />}
        loading={deleteThreat.isPending}
        disabled={deleteThreat.isPending}
        onClick={() => setShowConfirm(true)}
      >
        Delete Threat
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Threat</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this threat? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button intent="secondary" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              intent="negative"
              onClick={() => {
                setShowConfirm(false);
                deleteThreat.mutate();
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const ThreatViewTechniques = ({
  open,
  onOpenChange,
  aggregateSummary,
}: {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  aggregateSummary: AggregateTechniqueSummary;
}) => {
  const router = useRouterState();
  const queryClient = useQueryClient();
  const authConfig = useAuthConfig();
  const navigate = useNavigate();
  const { showExcepted } = useExceptedFilters();

  const { data: techniqueSummaries, isLoading } = useQuery(
    individualTechniqueSummaryQuery(
      queryClient,
      authConfig,
      aggregateSummary.threat?.techniques ?? EMPTY_ARRAY,
    ),
  );

  const technique = router.location.search.technique;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <div className="flex cursor-pointer items-center justify-between gap-2">
          <div className="flex flex-1 items-center gap-1 text-sm font-medium">
            <ShieldFile className="size-4 text-secondary-foreground" />
            {technique ?? "Policy Configurations"}
          </div>
          <div className="flex cursor-pointer items-center gap-1">
            {router.location.search.technique && (
              <Link
                to="/control-monitor"
                search={(prev) => ({ ...prev, technique: undefined })}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenChange(true);
                }}
                className="underline-offset-2 hover:underline"
              >
                Clear Filter
              </Link>
            )}
            <RotatingChevron active={open} />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <ThreatTechniquesTable
          isLoading={isLoading}
          techniques={techniqueSummaries ?? []}
          onSelect={async (summary: TechniqueSummary) => {
            await queryClient.prefetchQuery(
              aggregateTechniqueSummaryQuery(authConfig, {
                threatId: router.location.search.threat_id,
                focusTechnique: summary.id,
                filters: router.location.search.filters,
                showExcepted,
              }),
            );

            onOpenChange(false);

            void navigate({
              to: "/control-monitor",
              search: (prev) => ({
                ...prev,
                technique: summary.id,
              }),
            });
          }}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};

export function ThreatViewCard() {
  const authConfig = useAuthConfig();
  const router = useRouterState();
  const { showExcepted } = useExceptedFilters();

  const query = useSuspenseQuery(
    aggregateTechniqueSummaryQuery(authConfig, {
      threatId: router.location.search.threat_id,
      filters: router.location.search.filters,
      showExcepted,
    }),
  );

  if (!query.data.threat) {
    return <ThreatSelector />;
  }

  return <ThreatViewDetails />;
}

function ThreatTechniquesTable({
  isLoading,
  techniques,
  onSelect,
}: {
  isLoading: boolean;
  techniques: TechniqueSummary[];
  onSelect: (summary: TechniqueSummary) => void;
}) {
  const router = useRouterState();

  const rowSelection = useMemo(() => {
    if (!router.location.search.technique) {
      return {};
    }

    return {
      [router.location.search.technique]: true,
    };
  }, [router.location.search.technique]);

  const table = useTable({
    name: "threat-techniques",
    data: techniques,
    columns,
    state: {
      rowSelection,
    },
    initialState: {
      sorting: [{ id: "misconfiguration", desc: true }],
    },
    getRowId: (row) => row.id,
    onRowSelectionChange: (updater) => {
      const techniqueId = Object.keys(
        typeof updater === "function" ? updater({}) : updater,
      )[0];
      const match = techniques.find((t) => t.id === techniqueId);
      if (match) {
        onSelect(match);
      }
    },
  });

  return (
    <VirtualTable
      loading={isLoading}
      table={table}
      className="max-h-[200px]"
      selectedRowClassName="group selected bg-secondary-background/50 hover:bg-secondary-background/50 dark:bg-highlight dark:hover:bg-highlight"
      hideOptions
    />
  );
}

export function LoadingThreatCard() {
  return (
    <Card>
      <div className="my-1 h-3 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
      <div className="my-1 h-3 w-1/3 animate-pulse rounded-md bg-secondary-foreground" />

      <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
      <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />

      <div className="mt-8 h-2 w-20 animate-pulse rounded-md bg-secondary-foreground" />
      <div className="my-2 h-2 w-64 animate-pulse rounded-md bg-secondary-foreground" />
    </Card>
  );
}

export function LoadingThreatButton() {
  return (
    <Button
      className="min-h-[38px] ease-linear"
      icon={<RouteIcon className="text-secondary-foreground" />}
      intent="secondary"
      disabled
    >
      <span className="hidden md:block">Evaluate against a threat</span>
    </Button>
  );
}

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex max-h-[500px] w-[500px] flex-col border border-stroke-line bg-surface-background backdrop-blur ease-linear",
        className,
      )}
    >
      {children}
    </div>
  );
}
